import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosRequestConfig } from 'axios';
import { HttpStatusCode } from 'constantsValues';
import { CustomerDocument } from 'generated-api/bff/api';
import { getApiInstance } from 'services/Api';

// -----------------------------------------------------------------------------
// Thunks
// -----------------------------------------------------------------------------

/**
 * Fetch list of customer documents (BFF → GET /documents)
 */
export const fetchDocuments = createAsyncThunk<CustomerDocument[]>('documents/fetchDocuments', async () => {
	try {
		// The BFF exposes document endpoints under /documents
		const apiInstance = await getApiInstance('/commercial-selfservice/');
		const response = await apiInstance.documentApiServiceLayer.getDocuments();

		if (!response?.data?.documents?.length) {
			return;
		}

		return response.data.documents;
	} catch (error) {
		throw new Error('Failed to fetch documents', { cause: error });
	}
});

/**
 * Fetch a single policy PDF (BFF → GET /policies/{policy_no}/document)
 */
export const fetchPolicyDocument = createAsyncThunk<
	{ data: Blob; fileName: string },
	{ policyNo: string; docDate?: string }
>('documents/fetchPolicyDocument', async ({ policyNo, docDate }) => {
	const requestHeaders: AxiosRequestConfig = {
		headers: {
			'Content-Type': 'application/json',
		},
		responseType: 'blob',
	};

	try {
		// The BFF exposes policy documents under /policies
		const apiInstance = await getApiInstance('/commercial-selfservice/');
		const response = await apiInstance.documentApiServiceLayer.fetchPolicyDocument(policyNo, requestHeaders);

		const blob = new Blob([response.data as BlobPart], {
			type: 'application/pdf',
		});

		return {
			data: blob,
			fileName: `Police_fil_${docDate ?? ''}.pdf`,
		};
	} catch (error) {
		console.error('fetchPolicyDocument: Error fetching document', error);
		throw error;
	}
});

// -----------------------------------------------------------------------------
// Slice state
// -----------------------------------------------------------------------------

interface DocumentsState {
	data: CustomerDocument[] | null;
	documentBlob: Blob | null;
	status: HttpStatusCode | null;
	fetchDocumentStatus: HttpStatusCode | null;
	error: string | null;
}

const initialState: DocumentsState = {
	data: null,
	documentBlob: null,
	status: HttpStatusCode.Idle,
	fetchDocumentStatus: HttpStatusCode.Idle,
	error: null,
};

const documentSlice = createSlice({
	name: 'documents',
	initialState,
	reducers: {
		clearDocumentsCache(state) {
			state.data = null;
			state.status = HttpStatusCode.Idle;
			state.error = null;
		},
		clearDocumentBlob(state) {
			state.documentBlob = null;
			state.fetchDocumentStatus = HttpStatusCode.Idle;
		},
	},
	extraReducers: (builder) => {
		builder
			// ---------------------------------------------------------
			// fetchDocuments
			// ---------------------------------------------------------
			.addCase(fetchDocuments.pending, (state) => {
				state.status = HttpStatusCode.Loading;
				state.error = null;
			})
			.addCase(fetchDocuments.fulfilled, (state, action) => {
				if (!action.payload) {
					state.status = HttpStatusCode.NoContent;
					return;
				}

				state.status = HttpStatusCode.OK;
				state.data = action.payload;
			})
			.addCase(fetchDocuments.rejected, (state, action) => {
				state.status = HttpStatusCode.InternalServerError;
				state.error = action.error.message || 'Failed to fetch documents';
			})

			// ---------------------------------------------------------
			// fetchPolicyDocument
			// ---------------------------------------------------------
			.addCase(fetchPolicyDocument.pending, (state) => {
				state.fetchDocumentStatus = HttpStatusCode.Loading;
				state.error = null;
			})
			.addCase(fetchPolicyDocument.fulfilled, (state, action) => {
				state.fetchDocumentStatus = HttpStatusCode.OK;
				state.documentBlob = action.payload.data;
			})
			.addCase(fetchPolicyDocument.rejected, (state, action) => {
				state.fetchDocumentStatus = HttpStatusCode.InternalServerError;
				state.error = action.error.message || 'Failed to fetch document';
			});
	},
});

export const { clearDocumentsCache, clearDocumentBlob } = documentSlice.actions;
export default documentSlice.reducer;
